import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Stack, useTheme } from "@mui/material";
import { type FormEvent, useEffect } from "react";
import { type Control, type FieldValues, useForm } from "react-hook-form";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { ToolPreviousValues } from "web_myplann/Portfolio/Forms/ToolsShared/ToolPreviousValues";
import { ToolValueFields } from "web_myplann/Portfolio/Forms/ToolsShared/ToolValueFields";
import {
	getToolValueFormSchema,
	type ToolValueFormInput,
	type ToolValueFormOutput,
} from "web_myplann/Portfolio/Forms/ToolsShared/toolValueFormSchema";
import type { ToolPreviousValue } from "web_myplann/Portfolio/PortfolioShared/ManualValue/toolPreviousValueSchema";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";

type Props = {
	open: boolean;
	onClose: () => void;
	value: number | null;
	invested: number | null;
	valueDate: Date | null;
	previousValues: ToolPreviousValue[];
	onSave: (values: ToolValueFormOutput) => void;
};

const PageResources = Resources.Portfolio.AddTools;

export const ToolValueDrawer = ({
	open,
	onClose,
	value,
	invested,
	valueDate,
	previousValues,
	onSave,
}: Props) => {
	const { t } = useResource();
	const theme = useTheme();

	const form = useForm<ToolValueFormInput, unknown, ToolValueFormOutput>({
		resolver: zodResolver(getToolValueFormSchema(t)),
		mode: "onChange",
	});
	const hasPreviousValues = (form.watch("previousValues")?.length ?? 0) > 0;

	useEffect(() => {
		if (open) {
			form.reset({
				value,
				invested,
				valueDate: valueDate ?? new Date(),
				previousValues,
			});
		}
	}, [open, value, invested, valueDate, previousValues, form.reset]);

	const onSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.stopPropagation();
		void form.handleSubmit((values) => {
			onSave(values);
			onClose();
		})(event);
	};

	return (
		<MyPlannDrawer
			anchor="bottom"
			open={open}
			onClose={onClose}
			title={t(PageResources.updateValue)}
			grabber
		>
			<Stack component="form" onSubmit={onSubmit} pb={theme.spacingTokens.lg}>
				<Stack bgcolor={theme.palette.background.paper}>
					<ToolValueFields
						control={form.control as unknown as Control<FieldValues>}
						isLast={!hasPreviousValues}
					/>
					<ToolPreviousValues
						control={form.control as unknown as Control<FieldValues>}
					/>
				</Stack>
				<Stack px={theme.spacingTokens.lg} pt={theme.spacingTokens.lg}>
					<Button type="submit" variant="primary">
						{t(PageResources.done)}
					</Button>
				</Stack>
			</Stack>
		</MyPlannDrawer>
	);
};
